import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import SpinLoader from '@Utils/loaders/spin'

import { useLazyMeQuery } from '../../store/api'
import { useAppSelector } from '../../store/hooks'

function isOnboardingIncomplete(user: any) {
  return user?.is_onboarding_completed !== 1
}

export default function OnboardingGuard({
  redirectTo = '/onboarding',
}: { redirectTo?: string } = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const authSelector = useAppSelector((state) => state.auth)
  const [fetcher, { currentData, isError, isLoading }] = useLazyMeQuery({
    refetchOnFocus: false,
  })

  const hasUserData = useMemo(() => !!currentData, [currentData])

  useEffect(() => {
    // If not logged in, redirect to login
    if (!authSelector.access_token) {
      if (CURRENT_ENV === 'dev' || CURRENT_ENV === 'test') {
        navigate('/login')
      } else {
        window.location.replace(`${WORDPRESS_HOST}/login`)
      }
      return
    }

    // If no user data, fetch it
    if (!hasUserData && !isError && !isLoading) {
      fetcher()
        .unwrap()
        .then((response) => {
          // If onboarding incomplete and not on onboarding route, redirect to onboarding
          if (
            isOnboardingIncomplete(response) &&
            !location.pathname.includes('onboarding')
          ) {
            navigate(redirectTo)
          }
          // If onboarding completed and on onboarding route, redirect to home
          else if (
            !isOnboardingIncomplete(response) &&
            location.pathname.includes('onboarding')
          ) {
            navigate('/')
          }
        })
        .catch(() => {
          if (CURRENT_ENV === 'dev' || CURRENT_ENV === 'test') {
            navigate('/login')
          } else {
            window.location.replace(`${WORDPRESS_HOST}/login`)
          }
        })
      return
    }

    // If user data exists
    if (hasUserData) {
      // If onboarding incomplete and not on onboarding route, redirect to onboarding
      if (
        isOnboardingIncomplete(currentData) &&
        !location.pathname.includes('onboarding')
      ) {
        navigate(redirectTo)
      }
      // If onboarding completed and on onboarding route, redirect to home
      else if (
        !isOnboardingIncomplete(currentData) &&
        location.pathname.includes('onboarding')
      ) {
        navigate('/')
      }
    }
  }, [
    hasUserData,
    currentData,
    authSelector.access_token,
    location.pathname,
    isError,
    isLoading,
    navigate,
    redirectTo,
  ])

  if (isLoading) {
    return (
      <div className="pt-9">
        <SpinLoader />
      </div>
    )
  }

  return <Outlet />
}
